import React, { useState } from "react";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, isValidEmail } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

// Config-driven, progressive lead dialog.
// Step 1: low-friction contact (name/email/phone + consent) -> creates lead.
// Step 2: optional qualification fields -> PATCH qualify (skippable).
export const LeadDialog = ({
  open, onOpenChange, leadType, propertyId, title, subtitle,
  qualificationFields = [], ctaLabel = "Versturen", flow = "lead_dialog",
}) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", consent: false });
  const [qual, setQual] = useState({});

  const reset = () => { setStep(1); setLeadId(null); setContact({ name: "", email: "", phone: "", consent: false }); setQual({}); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submitContact = async () => {
    if (!contact.name.trim()) return toast.error("Vul je naam in.");
    if (!isValidEmail(contact.email)) return toast.error("Vul een geldig e-mailadres in.");
    if (!contact.phone.trim()) return toast.error("Vul je telefoonnummer in.");
    if (!contact.consent) return toast.error("Ga akkoord met de privacyverklaring om verder te gaan.");
    setBusy(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const res = await api("/leads", {
        method: "POST",
        body: JSON.stringify({
          lead_type: leadType, name: contact.name, email: contact.email,
          phone: contact.phone, property_id: propertyId || null, consent: true, source: "web",
          attribution: {
            source_page: window.location.pathname,
            referring_flow: flow,
            campaign: params.get("utm_source") || params.get("ref") || null,
            platform: "web",
          },
        }),
      });
      setLeadId(res.lead_id);
      if (qualificationFields.length === 0) { finishDone(); return; }
      setStep(2);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const submitQual = async () => {
    setBusy(true);
    try {
      await api(`/leads/${leadId}/qualify`, { method: "PATCH", body: JSON.stringify({ qualification: qual }) });
      finishDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const finishDone = () => {
    setStep(3);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid={`lead-dialog-${leadType}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-navy">{title}</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div><Label>Naam</Label><Input data-testid="lead-name" className="mt-1.5 rounded-xl" value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} /></div>
            <div><Label>E-mail</Label><Input data-testid="lead-email" type="email" className="mt-1.5 rounded-xl" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} /></div>
            <div><Label>Telefoon</Label><Input data-testid="lead-phone" className="mt-1.5 rounded-xl" value={contact.phone} onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))} /></div>
            <label className="flex items-start gap-2 text-xs text-slate-500">
              <input data-testid="lead-consent" type="checkbox" className="mt-0.5" checked={contact.consent} onChange={(e) => setContact((c) => ({ ...c, consent: e.target.checked }))} />
              <span>Ik geef toestemming om contact met mij op te nemen over deze aanvraag. Zie de <a href="/privacy" target="_blank" rel="noreferrer" className="text-brandblue underline">privacyverklaring</a>.</span>
            </label>
            <Button data-testid="lead-submit" onClick={submitContact} disabled={busy} className="w-full rounded-xl bg-brandblue py-6 hover:bg-brandblue/90">
              {busy ? <Loader2 className="animate-spin" size={18} /> : ctaLabel}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Een paar extra vragen helpen de makelaar je sneller en beter te helpen. Niet verplicht.</p>
            {qualificationFields.map((f) => (
              <div key={f.name}>
                <Label>{f.label}</Label>
                {f.type === "select" || f.type === "yesno" ? (
                  <Select value={qual[f.name] || ""} onValueChange={(v) => setQual((q) => ({ ...q, [f.name]: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid={`qual-${f.name}`}><SelectValue placeholder="Kies…" /></SelectTrigger>
                    <SelectContent>
                      {(f.options || (f.type === "yesno" ? [{ value: "ja", label: "Ja" }, { value: "nee", label: "Nee" }, { value: "gedeeltelijk", label: "Gedeeltelijk" }] : [])).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea className="mt-1.5 rounded-xl" data-testid={`qual-${f.name}`} value={qual[f.name] || ""} onChange={(e) => setQual((q) => ({ ...q, [f.name]: e.target.value }))} />
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} placeholder={f.placeholder} className="mt-1.5 rounded-xl" data-testid={`qual-${f.name}`} value={qual[f.name] || ""} onChange={(e) => setQual((q) => ({ ...q, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value }))} />
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" onClick={finishDone} className="flex-1 rounded-xl">Overslaan</Button>
              <Button data-testid="qual-submit" onClick={submitQual} disabled={busy} className="flex-1 rounded-xl bg-brandblue hover:bg-brandblue/90">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Versturen"}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="py-6 text-center" data-testid="lead-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success"><Check size={28} /></div>
            <h3 className="mt-4 font-display text-lg font-bold text-navy">Bedankt!</h3>
            <p className="mt-1 text-sm text-slate-500">Je aanvraag is verzonden. De makelaar neemt zo snel mogelijk contact met je op.</p>
            <Button onClick={() => close(false)} className="mt-5 rounded-xl bg-brandblue px-8 hover:bg-brandblue/90">Sluiten</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
